import {
  type SaveSolarDesignDraftInput,
  type SaveSolarDesignDraftMutation,
  type SolarDesignerStateQuery,
} from '@/gql/graphql';
import { produce } from 'immer';

/** Reflect the acknowledged save before any source-switch remount can resume it. */
export const applySavedSolarDesignDraft = (
  current: SolarDesignerStateQuery | undefined,
  saved: SaveSolarDesignDraftMutation['saveSolarDesignDraft'],
  input: SaveSolarDesignDraftInput
): SolarDesignerStateQuery | undefined => {
  if (!current) return current;
  const previous = current.projectById.solarDesignDraft;
  // A selected draft's save must never replace another draft's cache entry.
  if (previous && previous.id !== saved.id) return current;
  // A refetch may already have observed a newer save from another tab.
  if (previous && previous.revision > saved.revision) return current;
  const analysis =
    input.sourceAnalysisId == null
      ? null
      : [
          previous?.analysis,
          current.projectById.solarGeometryGeneration?.result,
        ].find(
          (source) =>
            source != null &&
            source.sourceAnalysisId === input.sourceAnalysisId &&
            source.sourceAnalysisVersion === input.sourceAnalysisVersion
        );
  return produce(current, (draft) => {
    draft.projectById.solarDesignDraft = {
      ...saved,
      basis: input.basis ?? previous?.basis ?? 'ANALYSIS',
      sourceAnalysisId: input.sourceAnalysisId ?? null,
      sourceAnalysisVersion: input.sourceAnalysisVersion ?? null,
      isUsedByEstimate: previous?.isUsedByEstimate ?? false,
      recommendedPanelAppliances: previous?.recommendedPanelAppliances ?? null,
      recommendedBatteryAppliances:
        previous?.recommendedBatteryAppliances ?? null,
      state: saved.state,
      isStale: false,
      staleReason: null,
      // A save is accepted only on the draft's current site; a refetch
      // records it for a draft saved for the first time.
      sourceSite: previous?.sourceSite ?? null,
      analysis: analysis ?? null,
    };
  });
};
